import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Loader, Select, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import type { RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';
import { can, formatDate, formatTime, type HistoryKind, type HistoryOp, type HistoryViewResponse } from '@parts/shared';
import { openUndoDialog } from '../../components/UndoLastAction';
import { useRole } from '../../role';
import { errorText, getJson } from '../../api';
import { PageHeader } from '../../components/layout/PageHeader';
import { TableGrid, type ColumnDefinition } from '../../components/table/TableGrid';
import { esc, person } from '../../components/table/cells';
import { t, tx, useI18n } from '../../i18n';
import { IconCollapse, IconExpand, IconSearch, IconUndo, IconX } from '../../icons';
import { getPref, setPref } from '../../settings';
import { useAppState } from '../../state';
import classes from '../requests/RequestsView.module.css';

interface TreeRow extends Record<string, unknown> {
  kind: 'op' | 'change';
  key: string;
  tree: string;
  _children?: TreeRow[];
}

type Filter = 'all' | HistoryKind;

/** Названия фильтров — ключи перевода. */
const FILTERS: { id: Filter; title: string }[] = [
  { id: 'all', title: 'Все' },
  { id: 'request', title: 'Запросы' },
  { id: 'purchase', title: 'Наличие и закупка' },
  { id: 'system', title: 'Прочее' }
];

const KIND_TONE: Record<HistoryKind, string> = { request: 'blue', purchase: 'green', system: 'gray' };

const PREF_COLLAPSED = 'history:collapsed';
const PREF_FILTER = 'history:filter';
const ALL_AUTHORS = '__all';

function opRow(op: HistoryOp): TreeRow {
  return {
    kind: 'op',
    key: op.id,
    tree: `${formatDate(op.at)}, ${formatTime(op.at)}`,
    author: op.author,
    action: op.action,
    tone: KIND_TONE[op.kind],
    reqNo: op.reqNo,
    name: tx(op.details),
    field: op.changes.length ? t('изменений: {n}', { n: op.changes.length }) : '',
    from: '',
    to: '',
    comment: op.comment,
    _children: op.changes.map((c, i) => ({
      kind: 'change',
      key: `${op.id}#${i}`,
      tree: c.art,
      author: '',
      action: '',
      tone: '',
      reqNo: '',
      name: c.name,
      field: tx(c.field),
      from: c.from,
      to: c.to,
      comment: ''
    }))
  };
}

/**
 * Экран «История»: каждое проведение — кто, когда, что сделал; внутри — изменения по позициям (было → стало).
 * Фильтры по типу действия и автору, поиск по OEM, № запроса и наименованию.
 */
export function HistoryScreen() {
  const { lang } = useI18n();
  const { state } = useAppState();
  const [ops, setOps] = useState<HistoryOp[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>(() => getPref<Filter>(PREF_FILTER, 'all'));
  const [author, setAuthor] = useState<string>(ALL_AUTHORS);
  const table = useRef<Tabulator | null>(null);
  const role = useRole();

  // Свёрнутые действия запоминаются для пользователя; по умолчанию всё развёрнуто
  const collapsed = useRef(new Set<string>(getPref<string[]>(PREF_COLLAPSED, [])));
  const searching = useRef(false);
  const bulk = useRef(false);
  searching.current = search.trim() !== '';
  const saveCollapsed = () => setPref(PREF_COLLAPSED, [...collapsed.current].slice(-500));

  const load = useCallback(async () => {
    try {
      const res = await getJson<HistoryViewResponse>('/api/views/history');
      setOps(res.ops);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, state?.snapshot.version]);

  const authors = useMemo(() => [...new Set((ops ?? []).map((o) => o.author).filter(Boolean))].sort(), [ops]);

  const counts = useMemo(() => {
    const out: Record<Filter, number> = { all: 0, request: 0, purchase: 0, system: 0 };
    for (const o of ops ?? []) {
      if (author !== ALL_AUTHORS && o.author !== author) continue;
      out.all++;
      out[o.kind]++;
    }
    return out;
  }, [ops, author]);

  const visible = useMemo(() => {
    if (!ops) return [];
    const q = search.trim().toUpperCase();
    return ops
      .filter((o) => (filter === 'all' || o.kind === filter) && (author === ALL_AUTHORS || o.author === author))
      .map((o) => {
        if (!q) return o;
        const head = [o.reqNo, o.action, o.details, o.comment, tx(o.action)].join(' ').toUpperCase().includes(q);
        const changes = head
          ? o.changes
          : o.changes.filter((c) => [c.art, c.name, c.field, tx(c.field), c.from, c.to].join(' ').toUpperCase().includes(q));
        return head || changes.length ? { ...o, changes } : null;
      })
      .filter((o): o is HistoryOp => !!o);
  }, [ops, search, filter, author]);

  // lang — строки пересобираются на новом языке
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo<TreeRow[]>(() => visible.map(opRow), [visible, lang]);

  const changeFilter = (f: Filter) => {
    setFilter(f);
    setPref(PREF_FILTER, f);
  };

  const setAll = (expand: boolean) => {
    const grid = table.current;
    if (!grid) return;
    bulk.current = true;
    for (const row of grid.getRows()) {
      const d = row.getData() as TreeRow;
      if (d.kind !== 'op' || !d._children?.length) continue;
      if (expand) row.treeExpand();
      else row.treeCollapse();
    }
    bulk.current = false;
    if (searching.current) return;
    if (expand) collapsed.current.clear();
    else for (const o of visible) collapsed.current.add(o.id);
    saveCollapsed();
  };

  const columns = useMemo<ColumnDefinition[]>(
    () => [
      {
        title: t('Время / OEM'),
        field: 'tree',
        width: 170,
        frozen: true,
        hozAlign: 'left',
        headerHozAlign: 'left',
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          return row.kind === 'op' ? `<span class="t-req">${esc(cell.getValue())}</span>` : esc(cell.getValue());
        }
      },
      { title: t('Автор'), field: 'author', width: 140, formatter: person },
      {
        title: t('Действие'),
        field: 'action',
        width: 175,
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          const v = String(cell.getValue() ?? '');
          return v ? `<span class="t-pill" data-tone="${esc(row.tone)}">${esc(tx(v))}</span>` : '';
        }
      },
      { title: t('№ запроса'), field: 'reqNo', width: 110 },
      {
        title: t('Наименование / подробности'),
        field: 'name',
        width: 240,
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          return row.kind === 'op' ? `<span class="t-meta">${esc(cell.getValue())}</span>` : esc(cell.getValue());
        }
      },
      {
        title: t('Что изменилось'),
        field: 'field',
        width: 165,
        formatter: (cell) => {
          const row = cell.getRow().getData() as TreeRow;
          return row.kind === 'op' ? `<span class="t-meta">${esc(cell.getValue())}</span>` : esc(cell.getValue());
        }
      },
      {
        title: t('Было'),
        field: 'from',
        width: 105,
        formatter: (cell) => {
          const v = String(cell.getValue() ?? '');
          const row = cell.getRow().getData() as TreeRow;
          if (row.kind === 'op') return '';
          return v ? `<span class="t-old">${esc(v)}</span>` : '<span class="t-dim">—</span>';
        }
      },
      {
        title: t('Стало'),
        field: 'to',
        width: 105,
        formatter: (cell) => {
          const v = String(cell.getValue() ?? '');
          const row = cell.getRow().getData() as TreeRow;
          if (row.kind === 'op') return '';
          return v ? `<span class="t-new">${esc(v)}</span>` : '<span class="t-dim">—</span>';
        }
      },
      { title: t('Комментарий'), field: 'comment', width: 170 }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  if (error)
    return (
      <div className={classes.page}>
        <PageHeader title={t('История')} />
        <div className={classes.body}>
          <Alert color="red" variant="light" title={t('Не удалось загрузить историю')}>
            {tx(error)}
          </Alert>
        </div>
      </div>
    );

  return (
    <div className={classes.page}>
      <PageHeader
        title={t('История')}
        zoom
        count={ops ? visible.length : undefined}
        subtitle={t('кто, когда и что изменил')}
        actions={
          <>
          {can(role, 'UNDO') && (
            <Button variant="default" leftSection={<IconUndo size={15} />} onClick={openUndoDialog}>
              {t('Отменить последнее действие')}
            </Button>
          )}
          <TextInput
            placeholder={t('OEM, № запроса, наименование')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftSection={<IconSearch size={15} />}
            rightSection={
              search ? (
                <UnstyledButton onClick={() => setSearch('')} aria-label={t('Очистить поиск')} className={classes.clear}>
                  <IconX size={14} />
                </UnstyledButton>
              ) : null
            }
            className={classes.search}
          />
          </>
        }
      />

      <div className={classes.body}>
        <div className={classes.bar}>
          <div className={classes.filters} role="tablist" aria-label={t('Тип действия')}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={classes.chip}
                data-active={filter === f.id || undefined}
                onClick={() => changeFilter(f.id)}
              >
                {t(f.title)}
                <span className={classes.chipCount}>{counts[f.id]}</span>
              </button>
            ))}
            <Select
              value={author}
              onChange={(v) => setAuthor(v ?? ALL_AUTHORS)}
              data={[{ value: ALL_AUTHORS, label: t('Все авторы') }, ...authors.map((a) => ({ value: a, label: tx(a) }))]}
              allowDeselect={false}
              w={190}
              size="sm"
              aria-label={t('Автор')}
            />
          </div>
          <div className={classes.treeBtns}>
            <Tooltip label={t('Развернуть все')}>
              <UnstyledButton className={classes.ghostBtn} onClick={() => setAll(true)}>
                <IconExpand size={15} />
                <span>{t('Развернуть')}</span>
              </UnstyledButton>
            </Tooltip>
            <Tooltip label={t('Свернуть все')}>
              <UnstyledButton className={classes.ghostBtn} onClick={() => setAll(false)}>
                <IconCollapse size={15} />
                <span>{t('Свернуть')}</span>
              </UnstyledButton>
            </Tooltip>
          </div>
        </div>

        <div className={classes.tableArea}>
          {!ops ? (
            <div className={classes.loading}>
              <Loader size="sm" />
              {t('Загружаю историю…')}
            </div>
          ) : (
            <TableGrid
              gridId="history"
              columns={columns}
              data={data}
              options={{
                dataTree: true,
                dataTreeStartExpanded: (row: RowComponent) => {
                  const d = row.getData() as TreeRow;
                  if (d.kind !== 'op') return false;
                  return searching.current || !collapsed.current.has(d.key);
                },
                dataTreeChildIndent: 22,
                dataTreeElementColumn: 'tree',
                index: 'key',
                placeholder: t('Нет действий — измените фильтр или поиск'),
                rowFormatter: (row) => {
                  const d = row.getData() as TreeRow;
                  row.getElement().classList.toggle('row-group', d.kind === 'op');
                }
              }}
              onReady={(instance) => {
                table.current = instance;
                const remember = (row: RowComponent, open: boolean) => {
                  if (bulk.current || searching.current) return;
                  const d = row.getData() as TreeRow;
                  if (d.kind !== 'op') return;
                  if (open) collapsed.current.delete(d.key);
                  else collapsed.current.add(d.key);
                  saveCollapsed();
                };
                instance.on('dataTreeRowExpanded', (row) => remember(row, true));
                instance.on('dataTreeRowCollapsed', (row) => remember(row, false));
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
